import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/ui/dialog';
import { Button } from '@ui/components/ui/button';
import IncidentFields from './IncidentFields';
import { IncidentDraft, SupportAgreement, isDraftValid } from './sources/incidents';

/** One incident, entered or corrected. */
const IncidentEditor = ({
  draft,
  agreement,
  isNew,
  products,
  productId,
  onProductChange,
  onChange,
  onSave,
  onCancel,
}: {
  draft: IncidentDraft | null;
  /** Absent on a product with no agreement, which has no targets to show against a priority. */
  agreement?: SupportAgreement | null;
  isNew: boolean;
  products?: { id: string; name: string }[];
  productId?: string;
  onProductChange?: (productId: string) => void;
  onChange: (draft: IncidentDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  // The product is fixed once an incident exists: moving it would move it to a different agreement, and
  // silently re-judge every figure already reported against the old one.
  const showProducts = !!products && !!onProductChange && isNew;

  return (
    <Dialog open={!!draft} onOpenChange={open => !open && onCancel()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t(isNew ? 'incidents.logIncident' : 'incidents.correctIncident')}</DialogTitle>
          <DialogDescription>{t('incidents.editorHint')}</DialogDescription>
        </DialogHeader>

        {draft && (
          <IncidentFields
            draft={draft}
            agreement={agreement}
            products={showProducts ? products : undefined}
            productId={showProducts ? productId : undefined}
            onProductChange={showProducts ? onProductChange : undefined}
            askTimestamps
            onChange={onChange}
          />
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t('actions.cancel')}
          </Button>
          <Button disabled={!draft || !isDraftValid(draft) || (showProducts && !productId)} onClick={onSave}>
            {t('actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IncidentEditor;
